import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Loader2, AlertCircle } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import apiService from '@/services/apiService';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setAuthUser, queueSessionId } = useApp();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing authentication...');

  useEffect(() => {
    // Prevent double processing (React StrictMode)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = window.location.hash;
        console.log('Auth callback - hash:', hash);
        
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('No session_id found in URL hash:', hash);
          setError('Authentication failed: No session ID received');
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        const sessionId = sessionIdMatch[1];
        console.log('Auth callback - extracted session_id:', sessionId.substring(0, 10) + '...');
        setStatus('Verifying session...');
        
        // Exchange session_id for session_token via backend
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        console.log('Auth callback - calling backend:', apiUrl);
        
        const response = await fetch(`${apiUrl}/api/auth/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        console.log('Auth callback - response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Auth callback - session exchange failed:', errorText);
          setError(`Authentication failed: ${response.status}`);
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        const userData = await response.json();
        console.log('Auth callback - user data received:', userData.email);
        setStatus('Logged in! Redirecting...');
        
        // Set user in context
        setAuthUser(userData);
        
        // Link any anonymous session analyses to this user
        if (queueSessionId) {
          try {
            await apiService.linkSessionsToUser([queueSessionId]);
            console.log('Linked anonymous session to user account');
          } catch (linkError) {
            console.warn('Could not link sessions:', linkError);
            // Don't fail the login if linking fails
          }
        }
        
        // Clear the hash from URL and navigate to dashboard
        window.history.replaceState(null, '', window.location.pathname);
        
        // Small delay to ensure state is persisted
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 100);
        
      } catch (error) {
        console.error('Auth callback error:', error);
        setError(`Authentication error: ${error.message}`);
        setTimeout(() => navigate('/'), 3000);
      }
    };

    processAuth();
  }, [navigate, setAuthUser, queueSessionId]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="flex items-center gap-2 mb-8">
          <Shield className="w-8 h-8 text-trust" />
          <span className="font-outfit font-bold text-2xl text-slate-900">Visa-Ready</span>
        </div>
        <div className="flex items-center gap-3 text-red-600 mb-4">
          <AlertCircle className="w-6 h-6" />
          <span className="font-public">{error}</span>
        </div>
        <p className="text-slate-500 text-sm">Redirecting to home page...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center">
      <div className="flex items-center gap-2 mb-8">
        <Shield className="w-8 h-8 text-trust" />
        <span className="font-outfit font-bold text-2xl text-slate-900">Visa-Ready</span>
      </div>
      <div className="flex items-center gap-3 text-slate-600">
        <Loader2 className="w-6 h-6 animate-spin text-trust" />
        <span className="font-public">{status}</span>
      </div>
    </div>
  );
};

export default AuthCallback;
